import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { get } from "lodash";

import { Modal, Button, Icon, Form, Loader, Message } from "semantic-ui-react";
import { connect } from "react-redux";
import { sendMessage } from "@Redux/Actions/chats";

import { useListRef } from "@Features/Monitor/Context";
import { messageBuilder } from "@Utils/functionality";

import Api from "@Api";
import ConfirmModal from "@Modals/ConfirmModal";

import styles from "./styles.module.css";

const contextVariables = [
	{ name: "companyName", label: "Nombre de la empresa", pattern: "{{company.name}}", type: "STRING", category: "Empresa" },
	{ name: "companyId", label: "ID de la empresa", pattern: "{{company.id}}", type: "NUMBER", category: "Empresa" },
	{ name: "branchName", label: "Nombre de sucursal", pattern: "{{sucursal.name}}", type: "STRING", category: "Sucursal" },
	{ name: "branchId", label: "ID de sucursal", pattern: "{{sucursal.id}}", type: "NUMBER", category: "Sucursal" },
	{ name: "contactName", label: "Nombre del contacto", pattern: "{{contact.name}}", type: "STRING", category: "Contacto" },
	{ name: "contactPhone", label: "Teléfono del contacto", pattern: "{{contact.phone}}", type: "PHONE", category: "Contacto" },
	{ name: "contactId", label: "ID del contacto", pattern: "{{contact.id}}", type: "NUMBER", category: "Contacto" },
	{ name: "contactCedula", label: "Cédula del contacto", pattern: "{{contact.cedula}}", type: "STRING", category: "Contacto" },
	{ name: "referenceCode", label: "Código de referencia", pattern: "{{contact.reference_code}}", type: "STRING", category: "Contacto" },
	{ name: "agentName", label: "Nombre del agente", pattern: "{{agent.name}}", type: "STRING", category: "Agente" },
	{ name: "agentId", label: "ID del agente", pattern: "{{agent.id}}", type: "NUMBER", category: "Agente" },
	{ name: "chatId", label: "ID del chat", pattern: "{{chat.id}}", type: "NUMBER", category: "Chat" },
	{ name: "currentDate", label: "Fecha actual", pattern: "{{currentDate}}", type: "DATE", category: "Sistema" },
	{ name: "currentTime", label: "Hora actual", pattern: "{{currentTime}}", type: "DATE", category: "Sistema" },
];

const getModesForType = () => {
	return [
		{ key: "manual", label: "Manual", icon: "pencil" },
		{ key: "auto", label: "Auto", icon: "magic" },
		{ key: "date", label: "Fecha", icon: "calendar" },
	];
};

const resolveContextVars = (text, context) => {
	if (!text) return text;
	const pathOf = {
		"{{contact.phone}}": ["chat", "contact", "phone"],
		"{{contact.name}}": ["chat", "contact", "name"],
		"{{contact.id}}": ["chat", "contact", "id"],
		"{{contact.cedula}}": ["chat", "contact", "cedula"],
		"{{contact.reference_code}}": ["chat", "contact", "reference_code"],
		"{{agent.name}}": ["agente", "name"],
		"{{agent.id}}": ["agente", "id"],
		"{{company.name}}": ["company", "name"],
		"{{company.id}}": ["company", "id"],
		"{{chat.id}}": ["chat", "id"],
		"{{sucursal.id}}": ["company", "sucId"],
		"{{sucursal.name}}": ["company", "sucId"],
	};
	const getVal = (pat) => {
		const segments = pathOf[pat];
		if (!segments) return "";
		let val = context;
		for (const p of segments) {
			if (!val || typeof val !== "object") return "";
			val = val[p];
		}
		if (val && typeof val === "object" && typeof val.then === "function") return "[Promise]";
		return String(val ?? "");
	};
	let result = text;
	contextVariables.forEach((v) => {
		const val = getVal(v.pattern);
		const nameKey = `{{${v.name}}}`;
		const patternKey = v.pattern;
		result = result.split(nameKey).join(val);
		if (patternKey !== nameKey) result = result.split(patternKey).join(val);
	});
	return result;
};

const VarField = ({ label, placeholder, rows, value, onChange }) => {
	const inputRef = useRef(null);

	return (
		<div className={styles.varField}>
			<label>{label}</label>
			{rows ? (
				<textarea
					ref={inputRef}
					rows={rows}
					placeholder={placeholder}
					value={value}
					onChange={(e) => {
						onChange(e.target.value);
					}}
				/>
			) : (
				<input
					ref={inputRef}
					placeholder={placeholder}
					value={value}
					onChange={(e) => {
						onChange(e.target.value);
					}}
				/>
			)}
		</div>
	);
};

VarField.propTypes = {
	label: PropTypes.string,
	placeholder: PropTypes.string,
	rows: PropTypes.number,
	value: PropTypes.string,
	onChange: PropTypes.func,
};

const BodyAutocomplete = ({ label, placeholder, rows, value, onChange, activeVars }) => {
	const textareaRef = useRef(null);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [suggestions, setSuggestions] = useState([]);
	const [cursorPos, setCursorPos] = useState(0);

	const handleChange = (e) => {
		const val = e.target.value;
		const pos = e.target.selectionStart;
		setCursorPos(pos);
		onChange(val);

		const textBefore = val.substring(0, pos);
		const openBraces = textBefore.lastIndexOf("{{");

		if (openBraces !== -1) {
			const query = textBefore.substring(openBraces + 2);
			const filtered = activeVars.filter((v) => {
				return v.pattern.toLowerCase().includes(query.toLowerCase()) || v.label.toLowerCase().includes(query.toLowerCase());
			});
			if (filtered.length > 0) {
				setSuggestions(filtered);
				setShowSuggestions(true);
			} else {
				setShowSuggestions(false);
			}
		} else {
			setShowSuggestions(false);
		}
	};

	const handleSelect = (variable) => {
		const text = value || "";
		const openBraces = text.substring(0, cursorPos).lastIndexOf("{{");
		const before = text.substring(0, openBraces);
		const after = text.substring(cursorPos);
		const newText = `${before}${variable.pattern}${after}`;
		onChange(newText);
		setShowSuggestions(false);

		setTimeout(() => {
			if (textareaRef.current) {
				const newPos = openBraces + variable.pattern.length;
				textareaRef.current.focus();
				textareaRef.current.setSelectionRange(newPos, newPos);
			}
		}, 0);
	};

	const handleKeyDown = (e) => {
		if (e.key === "Escape") {
			setShowSuggestions(false);
		}
	};

	return (
		<div className={styles.varField} style={{ position: "relative" }}>
			<label>{label}</label>
			<textarea
				ref={textareaRef}
				rows={rows}
				placeholder={placeholder}
				value={value}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				onBlur={() => {
					setTimeout(() => {
						setShowSuggestions(false);
					}, 150);
				}}
			/>
			{showSuggestions && suggestions.length > 0 && (
				<div className={styles.autocompleteDropdown}>
					{suggestions.map((v) => {
						return (
							<div
								key={v.name}
								role="button"
								tabIndex={0}
								className={styles.autocompleteItem}
								onMouseDown={(e) => {
									e.preventDefault();
									handleSelect(v);
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										handleSelect(v);
									}
								}}>
								<span className={styles.autocompleteLabel}>{v.label}</span>
								<span className={styles.autocompletePattern}>{v.pattern}</span>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

BodyAutocomplete.propTypes = {
	label: PropTypes.string,
	placeholder: PropTypes.string,
	rows: PropTypes.number,
	value: PropTypes.string,
	onChange: PropTypes.func,
	activeVars: PropTypes.array,
};

/* ========== Variable Card Sub-components ========== */

const ValueModeSelect = ({ value, onChange }) => {
	const modes = getModesForType();
	return (
		<div className={styles.varModeTabs}>
			{modes.map((m) => {
				return (
					<button
						type="button"
						key={m.key}
						className={`${styles.varModeTab} ${value === m.key ? styles.varModeTabActive : ""}`}
						onClick={() => {
							return onChange(m.key);
						}}>
						<Icon name={m.icon} size="tiny" /> {m.label}
					</button>
				);
			})}
		</div>
	);
};

ValueModeSelect.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func,
};

const ManualValueInput = ({ type, value, onChange }) => {
	const inputType = {
		STRING: "text",
		NUMBER: "number",
		EMAIL: "email",
		PHONE: "tel",
		DATE: "date",
	};

	if (type === "BOOLEAN") {
		const boolVal = value === true || value === "true";
		return (
			<div className={styles.varSwitchRow}>
				<label className={styles.varSwitchLabel}>Valor</label>
				<label className={styles.varSwitch}>
					<input
						type="checkbox"
						checked={boolVal}
						onChange={(e) => {
							return onChange(e.target.checked);
						}}
					/>
					<span className={styles.varSwitchSlider} />
				</label>
				<span className={styles.varSwitchText}>{boolVal ? "Verdadero" : "Falso"}</span>
			</div>
		);
	}

	if (type === "OBJECT" || type === "ARRAY") {
		return (
			<div className={styles.varManualInput}>
				<label className={styles.varInputLabel}>Valor JSON</label>
				<textarea
					className={styles.varJsonEditor}
					rows={4}
					placeholder={type === "ARRAY" ? "[ \"item1\", \"item2\" ]" : "{ \"key\": \"value\" }"}
					value={value || ""}
					onChange={(e) => {
						return onChange(e.target.value);
					}}
				/>
			</div>
		);
	}

	const placeholders = {
		STRING: "Escribe un valor...",
		NUMBER: "0",
		EMAIL: "correo@ejemplo.com",
		PHONE: "+1 809 000 0000",
		DATE: "",
	};

	return (
		<div className={styles.varManualInput}>
			<label className={styles.varInputLabel}>Valor</label>
			<input
				className={styles.varTextInput}
				type={inputType[type] || "text"}
				placeholder={placeholders[type] || ""}
				value={value || ""}
				onChange={(e) => {
					return onChange(e.target.value);
				}}
			/>
		</div>
	);
};

ManualValueInput.propTypes = {
	type: PropTypes.string,
	value: PropTypes.any,
	onChange: PropTypes.func,
};

const AutoVariableSelector = ({ value, onChange }) => {
	const [search, setSearch] = useState("");
	const [open, setOpen] = useState(false);
	const searchRef = useRef(null);
	const listRef = useRef(null);

	const categories = {};
	contextVariables.forEach((v) => {
		const cat = v.category || "Otros";
		if (!categories[cat]) categories[cat] = [];
		categories[cat].push(v);
	});

	const filtered = {};
	Object.keys(categories).forEach((cat) => {
		const vars = categories[cat].filter((v) => {
			const q = search.toLowerCase();
			return v.label.toLowerCase().includes(q) || v.name.toLowerCase().includes(q) || v.pattern.toLowerCase().includes(q);
		});
		if (vars.length) filtered[cat] = vars;
	});

	const selectedVar = contextVariables.find((v) => {
		return v.pattern === value;
	});

	useEffect(() => {
		if (open && searchRef.current) {
			searchRef.current.focus();
		}
	}, [open]);

	const handleSelect = (v) => {
		onChange(v.pattern);
		setOpen(false);
		setSearch("");
	};

	return (
		<div className={styles.varAutoSelector}>
			<div className={styles.varInputLabel}>Variable</div>
			{!open ? (
				<div
					role="button"
					tabIndex={0}
					className={styles.varAutoTrigger}
					onClick={() => {
						setOpen(true);
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							setOpen(true);
						}
					}}>
					{selectedVar ? (
						<span className={styles.varAutoSelected}>
							<Icon name="linkify" size="small" /> {selectedVar.label}
							<small>{selectedVar.pattern}</small>
						</span>
					) : (
						<span className={styles.varAutoPlaceholder}>
							<Icon name="search" size="small" /> Seleccionar variable...
						</span>
					)}
					{selectedVar ? (
						<Icon
							name="remove circle"
							size="small"
							link
							onClick={(e) => {
								e.stopPropagation();
								onChange("");
							}}
						/>
					) : (
						<Icon name="chevron down" size="small" />
					)}
				</div>
			) : (
				<div className={styles.varAutoPopover}>
					<div className={styles.varAutoSearchRow}>
						<input
							ref={searchRef}
							className={styles.varAutoSearch}
							type="text"
							placeholder="Buscar variable..."
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
							}}
							onKeyDown={(e) => {
								if (e.key === "Escape") {
									setOpen(false);
									setSearch("");
								}
							}}
						/>
						<button
							type="button"
							className={styles.varAutoClose}
							onClick={() => {
								setOpen(false);
								setSearch("");
							}}>
							<Icon name="close" size="small" />
						</button>
					</div>
					<div className={styles.varAutoList} ref={listRef}>
						{Object.keys(filtered).length === 0 ? (
							<div className={styles.varAutoEmpty}>No se encontraron variables</div>
						) : (
							Object.keys(filtered).map((cat) => {
								return (
									<div key={cat}>
										<div className={styles.varAutoCat}>{cat}</div>
										{filtered[cat].map((v) => {
											return (
												<div
													role="button"
													tabIndex={0}
													key={v.name}
													className={`${styles.varAutoItem} ${value === v.pattern ? styles.varAutoItemActive : ""}`}
													onClick={() => {
														handleSelect(v);
													}}
													onKeyDown={(e) => {
														if (e.key === "Enter" || e.key === " ") {
															e.preventDefault();
															handleSelect(v);
														}
													}}>
													<span className={styles.varAutoItemLabel}>{v.label}</span>
													<span className={styles.varAutoItemPattern}>{v.pattern}</span>
												</div>
											);
										})}
									</div>
								);
							})
						)}
					</div>
				</div>
			)}
		</div>
	);
};

AutoVariableSelector.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func,
};

const DateValueSelector = ({ value, onChange, maxDate }) => {
	const [showCalendar, setShowCalendar] = useState(false);
	const today = new Date().toISOString().split("T")[0];
	const max = maxDate || today;
	const isToday = value === today;
	const isCustom = value && value !== today;

	return (
		<div className={styles.varDateSelector}>
			<div className={styles.varInputLabel}>Fecha</div>
			<div className={styles.varDateRow}>
				<button
					type="button"
					className={`${styles.varDateBtn} ${isToday ? styles.varDateBtnActive : ""}`}
					onClick={() => {
						return onChange(today);
					}}>
					<Icon name="clock" size="small" /> Fecha actual
				</button>
				<button
					type="button"
					className={`${styles.varDateBtn} ${isCustom ? styles.varDateBtnActive : ""}`}
					onClick={() => {
						return setShowCalendar(!showCalendar);
					}}>
					<Icon name="calendar" size="small" /> Personalizar
				</button>
			</div>
			{showCalendar && (
				<div className={styles.varDatePickerWrap}>
					<input
						type="date"
						className={styles.varDatePicker}
						max={max}
						value={value || today}
						onChange={(e) => {
							onChange(e.target.value);
							setShowCalendar(false);
						}}
					/>
				</div>
			)}
			{value && (
				<div className={styles.varDatePreview}>
					<Icon name="check circle" size="small" color="blue" /> {value}
				</div>
			)}
		</div>
	);
};

DateValueSelector.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func,
	maxDate: PropTypes.string,
};

const VariableCard = ({ variable, config, onModeChange, onValueChange, maxDate, onRemove }) => {
	const mode = config?.mode || "auto";
	const rawValue = config?.value;
	const value = rawValue !== undefined && rawValue !== null ? rawValue : "";
	const hasValue = value !== "";

	return (
		<div className={`${styles.varCard} ${hasValue ? styles.varCardActive : ""}`}>
			<div className={styles.varCardHeader}>
				<div className={styles.varCardHeaderLeft}>
					{hasValue && <div className={styles.varActiveDot} />}
					<div className={`${styles.varBadge} ${styles[`varBadge${variable.type}`]}`}>{variable.type}</div>
				</div>
				<div className={styles.varCardHeaderRight}>
					<ValueModeSelect value={mode} onChange={onModeChange} />
					{onRemove && (
						<button
							type="button"
							className={styles.varCardRemove}
							onClick={() => {
								return onRemove(variable.name);
							}}>
							<Icon name="remove circle" size="small" />
						</button>
					)}
				</div>
			</div>
			<div className={styles.varCardBody}>
				<div className={styles.varCardLabel}>{variable.label}</div>
				<div className={styles.varCardPattern}>{`{{${variable.name}}}`}</div>
			</div>
			<div className={styles.varCardValue}>
				{mode === "manual" && <ManualValueInput type={variable.type} value={value} onChange={onValueChange} />}
				{mode === "auto" && <AutoVariableSelector value={value} onChange={onValueChange} />}
				{mode === "date" && <DateValueSelector value={value} onChange={onValueChange} maxDate={maxDate} />}
			</div>
		</div>
	);
};

VariableCard.propTypes = {
	variable: PropTypes.object,
	config: PropTypes.object,
	onModeChange: PropTypes.func,
	onValueChange: PropTypes.func,
	maxDate: PropTypes.string,
	onRemove: PropTypes.func,
};

/* ========== Main Modal ========== */

const FlowSendModal = ({ open, onClose, flow, currentChat, dispatch, userName, onSuccessSend }) => {
	const { listRef } = useListRef();
	const [header, setHeader] = useState("");
	const [body, setBody] = useState("");
	const [footer, setFooter] = useState("");
	const [flowAction, setFlowAction] = useState("navigate");
	const [flowCta, setFlowCta] = useState("");
	const [screen, setScreen] = useState("");
	const [screens, setScreens] = useState([]);
	const [flowActions] = useState(["navigate", "data_exchange"]);
	const [varConfigs, setVarConfigs] = useState({});
	const [selectedVars, setSelectedVars] = useState([]);
	const [dataSchema, setDataSchema] = useState([]);
	const [sending, setSending] = useState(false);
	const [savingConfig, setSavingConfig] = useState(false);
	const [error, setError] = useState(null);
	const [detailLoaded, setDetailLoaded] = useState(false);

	const [autoSend, setAutoSend] = useState(false);
	const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
	const initialValuesRef = useRef(null);

	const schemaVariables = useMemo(() => {
		const arr = Array.isArray(dataSchema) ? dataSchema : [];
		return arr.map((f) => {
			return {
				name: f.name,
				label: f.name,
				pattern: `{{${f.name}}}`,
				type: (f.type || "STRING").toUpperCase(),
				category: "Datos del Flow",
				required: f.required === true,
				isSchemaField: true,
			};
		});
	}, [dataSchema]);

	const allVariables = useMemo(() => {
		const existing = new Set(
			contextVariables.map((v) => {
				return v.name;
			})
		);
		const extras = schemaVariables.filter((sv) => {
			return !existing.has(sv.name);
		});
		return [...contextVariables, ...extras];
	}, [schemaVariables]);

	// eslint-disable-next-line no-unused-vars
	const saveInitialValues = useCallback(() => {
		initialValuesRef.current = {
			header,
			body,
			footer,
			flowAction,
			flowCta,
			screen,
			varConfigs: JSON.stringify(varConfigs),
			selectedVars: JSON.stringify(selectedVars),
		};
	}, [header, body, footer, flowAction, flowCta, screen, varConfigs, selectedVars]);

	const hasChanges = useCallback(() => {
		if (!initialValuesRef.current) return false;
		const init = initialValuesRef.current;
		return (
			header !== init.header ||
			body !== init.body ||
			footer !== init.footer ||
			flowAction !== init.flowAction ||
			flowCta !== init.flowCta ||
			screen !== init.screen ||
			JSON.stringify(varConfigs) !== init.varConfigs ||
			JSON.stringify(selectedVars) !== init.selectedVars
		);
	}, [header, body, footer, flowAction, flowCta, screen, varConfigs, selectedVars]);

	const handleClose = useCallback(() => {
		if (hasChanges()) {
			setConfirmCloseOpen(true);
		} else {
			onClose();
		}
	}, [hasChanges, onClose]);

	const handleConfirmClose = useCallback(() => {
		setConfirmCloseOpen(false);
		onClose();
	}, [onClose]);

	useEffect(() => {
		if (open && flow) {
			if (flow.directSend) setAutoSend(true);
			loadFlowDetail();
		}
	}, [open, flow]);

	useEffect(() => {
		if (autoSend && detailLoaded && !sending && !error) {
			setAutoSend(false);
			handleSend();
		}
	}, [autoSend, detailLoaded]);

	const loadFlowDetail = async () => {
		setDetailLoaded(false);
		setError(null);
		try {
			let d = null;
			if (flow?.flow || flow?.settings) {
				d = flow;
			} else {
				const res = await Api.get(`/flows/${flow.id}`);
				d = res?.data?.data;
			}
			if (d) {
				let parsedScreens = ["GENERAL"];
				let schema = [];
				if (d.flow) {
					try {
						const parsed = typeof d.flow === "string" ? JSON.parse(d.flow) : d.flow;
						console.log("[FlowSendModal] parsed flow:", parsed); // eslint-disable-line no-console
						parsedScreens = (parsed.screens || []).map((s) => {
							return s.id;
						});
						const firstScreen = parsed.screens?.[0];
						let raw = firstScreen?.data;

						// try alternative locations if data is empty
						if (!raw || (typeof raw === "object" && Object.keys(raw).length === 0)) {
							raw = firstScreen?.layout?.data;
						}
						if (!raw || (typeof raw === "object" && Object.keys(raw).length === 0)) {
							raw = firstScreen?.components?.[0]?.data;
						}

						if (Array.isArray(raw)) {
							schema = raw.map((f) => {
								return {
									name: f.name,
									type: (f.type || "STRING").toUpperCase(),
									required: f.required === true,
								};
							});
						} else if (raw && typeof raw === "object") {
							schema = Object.keys(raw).map((key) => {
								return {
									name: key,
									type: (raw[key]?.type || "STRING").toUpperCase(),
									required: true,
								};
							});
						} else {
							schema = [];
						}
						console.log("[FlowSendModal] schema final:", schema); // eslint-disable-line no-console
					} catch (e) {
						/* ignore */
					}
				}
				setDataSchema(schema);

				let settings = {};
				if (d.settings) {
					try {
						settings = typeof d.settings === "string" ? JSON.parse(d.settings) : d.settings;
					} catch (e) {
						/* ignore */
					}
				}
				setScreens(parsedScreens.length ? parsedScreens : ["GENERAL"]);
				setScreen(settings.screen || parsedScreens[0] || "GENERAL");
				setFlowAction(settings.flowAction || "navigate");
				setHeader(settings.header || "");
				setBody(settings.body || "");
				setFooter(settings.footer || "");
				setFlowCta(settings.flowCta || "");

				const savedData = settings.data || {};
				const configs = {};
				const selected = [];

				const existingNames = new Set(
					contextVariables.map((v) => {
						return v.name;
					})
				);
				const mergedSchema = schema
					.filter((f) => {
						return !existingNames.has(f.name);
					})
					.map((f) => {
						return {
							name: f.name,
							isSchemaField: true,
							required: f.required === true,
						};
					});
				const mergedVars = [...contextVariables, ...mergedSchema];

				mergedVars.forEach((v) => {
					if (savedData[v.name] !== undefined || (v.isSchemaField && v.required)) {
						selected.push(v.name);
						const val = savedData[v.name];
						if (v.isSchemaField) {
							configs[v.name] = { mode: "manual", value: val };
						} else if (val && val.startsWith && val.startsWith("{{") && val.endsWith("}}")) {
							configs[v.name] = { mode: "auto", value: val };
						} else {
							configs[v.name] = { mode: "manual", value: val };
						}
					}
				});
				setVarConfigs(configs);
				setSelectedVars(selected);

				initialValuesRef.current = {
					header: settings.header || "",
					body: settings.body || "",
					footer: settings.footer || "",
					flowAction: settings.flowAction || "navigate",
					flowCta: settings.flowCta || "",
					screen: settings.screen || parsedScreens[0] || "GENERAL",
					varConfigs: JSON.stringify(configs),
					selectedVars: JSON.stringify(selected),
				};
			}
		} catch (e) {
			setScreens(["GENERAL"]);
			setScreen("GENERAL");
		} finally {
			setDetailLoaded(true);
		}
	};

	const handleVarModeChange = (varName, newMode) => {
		setVarConfigs((prev) => {
			return {
				...prev,
				[varName]: {
					mode: newMode,
					value: "",
				},
			};
		});
	};

	const handleVarValueChange = (varName, newValue) => {
		setVarConfigs((prev) => {
			return {
				...prev,
				[varName]: { ...prev[varName], value: newValue },
			};
		});
	};

	const handleAddVar = (varName) => {
		if (selectedVars.includes(varName)) return;
		const variable = allVariables.find((v) => {
			return v.name === varName;
		});
		setSelectedVars((prev) => {
			return [...prev, varName];
		});
		setVarConfigs((prev) => {
			if (variable?.isSchemaField) {
				return { ...prev, [varName]: { mode: "manual", value: "" } };
			}
			return { ...prev, [varName]: { mode: "auto", value: variable?.pattern || "" } };
		});
	};

	const handleRemoveVar = (varName) => {
		setSelectedVars((prev) => {
			return prev.filter((n) => {
				return n !== varName;
			});
		});
		setVarConfigs((prev) => {
			const next = { ...prev };
			delete next[varName];
			return next;
		});
	};

	const buildDataFromConfigs = () => {
		const resolvedData = {};
		const context = {
			chat: currentChat,
			company: { name: get(currentChat, "channel.label", ""), id: get(currentChat, "channelId", "") },
			agente: { name: userName },
		};
		const varTypeMap = {};
		allVariables.forEach((v) => {
			varTypeMap[v.name] = v.type;
		});
		const pathOf = {
			"{{contact.phone}}": ["chat", "contact", "phone"],
			"{{contact.name}}": ["chat", "contact", "name"],
			"{{contact.id}}": ["chat", "contact", "id"],
			"{{contact.cedula}}": ["chat", "contact", "cedula"],
			"{{contact.reference_code}}": ["chat", "contact", "reference_code"],
			"{{agent.name}}": ["agente", "name"],
			"{{agent.id}}": ["agente", "id"],
			"{{company.name}}": ["company", "name"],
			"{{company.id}}": ["company", "id"],
			"{{chat.id}}": ["chat", "id"],
			"{{sucursal.id}}": ["company", "sucId"],
			"{{sucursal.name}}": ["company", "sucId"],
		};

		Object.keys(varConfigs).forEach((k) => {
			const cfg = varConfigs[k];
			if (!cfg || cfg.value === undefined || cfg.value === null) return;
			if (cfg.value === "" && varTypeMap[k] !== "BOOLEAN") return;

			if (cfg.mode === "auto") {
				const segments = pathOf[cfg.value];
				if (!segments) return;
				let val = context;
				for (const p of segments) {
					if (!val || typeof val !== "object") {
						val = "";
						break;
					}
					val = val[p];
				}
				if (val && typeof val === "object" && typeof val.then === "function") val = "[Promise]";
				resolvedData[k] = String(val ?? "");
			} else if (varTypeMap[k] === "BOOLEAN") {
				resolvedData[k] = cfg.value === true || cfg.value === "true";
			} else {
				resolvedData[k] = String(cfg.value);
			}
		});
		return resolvedData;
	};

	const handleSend = async () => {
		if (!flow) return;
		setSending(true);
		setError(null);
		try {
			const context = {
				chat: currentChat,
				company: { name: get(currentChat, "channel.label", ""), id: get(currentChat, "channelId", "") },
				agente: { name: userName },
			};
			const resolvedBody = resolveContextVars(body, context);
			const resolvedFooter = resolveContextVars(footer, context);
			const resolvedHeader = resolveContextVars(header, context);
			const resolvedData = buildDataFromConfigs();

			const { flowId } = flow;
			const flowName = flow.name || flow.label || flowId;

			const interactive = {
				type: "interactive",
				interactive: {
					type: "flow",
					header: { type: "text", text: resolvedHeader || flowName },
					body: { text: resolvedBody },
					footer: { text: resolvedFooter },
					action: {
						name: "flow",
						parameters: {
							flow_message_version: "3",
							flow_action: flowAction,
							flow_id: flowId,
							mode: "published",
							flow_cta: flowCta || flowName,
							flow_action_payload: {
								screen,
								data: resolvedData,
							},
						},
					},
				},
			};

			const newMessage = messageBuilder({
				chat: currentChat,
				userName,
				text: JSON.stringify(interactive),
				type: "JSON",
			});

			const result = await dispatch(sendMessage({ message: newMessage, listRef }));
			if (result !== false) {
				if (onSuccessSend) onSuccessSend();
				onClose();
			}
		} catch (err) {
			setError(err?.message || "Error al enviar el flow");
		} finally {
			setSending(false);
		}
	};

	const handleSaveConfig = async () => {
		if (!flow) return;
		setSavingConfig(true);
		setError(null);
		try {
			const varTypeMap = {};
			allVariables.forEach((v) => {
				varTypeMap[v.name] = v.type;
			});
			const dataToSave = {};
			Object.keys(varConfigs).forEach((k) => {
				const cfg = varConfigs[k];
				if (!cfg || cfg.value === undefined || cfg.value === null) return;
				if (cfg.value === "" && varTypeMap[k] !== "BOOLEAN") return;
				dataToSave[k] = varTypeMap[k] === "BOOLEAN" ? cfg.value === true || cfg.value === "true" : cfg.value;
			});
			const res = await Api.patch(`/flows/${flow.id}/settings`, {
				settings: { header, body, footer, flowAction, flowCta, screen, data: dataToSave },
			});
			if (res?.data?.success) {
				onClose();
			} else {
				setError(res?.data?.message || "Error al guardar configuración");
			}
		} catch (err) {
			setError(err?.response?.data?.message || "Error al guardar configuración");
		} finally {
			setSavingConfig(false);
		}
	};

	const screenOptions = screens.map((s) => {
		return { key: s, value: s, text: s };
	});
	const flowActionOptions = flowActions.map((a) => {
		return { key: a, value: a, text: a };
	});

	return (
		<>
			<Modal open={open} onClose={handleClose} closeIcon centered dimmer="blurring" style={{ maxHeight: "90vh", width: "650px" }}>
				<Modal.Header style={{ backgroundColor: "var(--header)", color: "#fff", borderBottom: "none" }}>
					<Icon name="send" /> {flow?.name || flow?.label || "Enviar Flow"}
				</Modal.Header>
				<Modal.Content style={{ maxHeight: "calc(90vh - 160px)", overflowY: "auto" }}>
					{error && <Message error content={error} style={{ borderRadius: 10 }} />}
					{!detailLoaded || autoSend ? (
						<Loader active inline="centered" content={autoSend ? "Enviando flow..." : undefined} />
					) : (
						<Form>
							<VarField label="Encabezado" placeholder="Texto del encabezado" value={header} onChange={setHeader} />
							<BodyAutocomplete
								label="Cuerpo"
								placeholder="Escribe {{ para buscar variables..."
								rows={3}
								value={body}
								onChange={setBody}
								activeVars={allVariables.filter((v) => {
									return varConfigs[v.name]?.value;
								})}
							/>
							<VarField label="Pie" placeholder="Texto del pie" value={footer} onChange={setFooter} />
							<Form.Dropdown
								label="Acción del flow"
								placeholder="Selecciona acción"
								selection
								options={flowActionOptions}
								value={flowAction}
								onChange={(e, { value }) => {
									return setFlowAction(value);
								}}
							/>
							<VarField label="Texto del botón" placeholder="Ej: Iniciar encuesta" value={flowCta} onChange={setFlowCta} />
							<Form.Dropdown
								label="Pantalla"
								placeholder="Selecciona pantalla"
								selection
								options={screenOptions}
								value={screen}
								onChange={(e, { value }) => {
									return setScreen(value);
								}}
								loading={!detailLoaded}
							/>
							<Form.Field>
								<label className={styles.varSectionTitle}>Data (variables de contexto)</label>
								<small className={styles.flowDataHelper}>Selecciona las variables que se enviarán al flow.</small>
								<div className={styles.varAddDropdown}>
									<div className={styles.varAddIcon}>
										<Icon name="plus circle" size="small" />
									</div>
									<select
										className={styles.varAddSelect}
										value=""
										onChange={(e) => {
											if (e.target.value) {
												handleAddVar(e.target.value);
											}
										}}>
										<option value="" disabled>
											Seleccionar variable...
										</option>
										{allVariables
											.filter((v) => {
												return !selectedVars.includes(v.name);
											})
											.map((v) => {
												return (
													<option key={v.name} value={v.name}>
														{v.label} ({v.type})
													</option>
												);
											})}
									</select>
								</div>
								<div className={styles.varGrid}>
									{selectedVars.map((name) => {
										const v = allVariables.find((cv) => {
											return cv.name === name;
										});
										if (!v) return null;
										return (
											<div key={v.name} className={styles.varCardEnter}>
												<VariableCard
													variable={v}
													config={varConfigs[v.name]}
													maxDate={new Date().toISOString().split("T")[0]}
													onModeChange={(mode) => {
														return handleVarModeChange(v.name, mode);
													}}
													onValueChange={(val) => {
														return handleVarValueChange(v.name, val);
													}}
													onRemove={handleRemoveVar}
												/>
											</div>
										);
									})}
								</div>
							</Form.Field>
						</Form>
					)}
				</Modal.Content>
				<Modal.Actions style={{ background: "#fff", borderTop: "1px solid #e8e8e8", display: "flex", justifyContent: "space-between" }}>
					<Button
						loading={savingConfig}
						onClick={handleSaveConfig}
						disabled={!detailLoaded}
						className={`${styles.modalBtn} ${styles.modalBtnGray}`}>
						<Icon name="save" /> Guardar configuración
					</Button>
					<div style={{ display: "flex", gap: "8px" }}>
						<Button onClick={handleClose} className={`${styles.modalBtn} ${styles.modalBtnRed}`}>
							Cancelar
						</Button>
						<Button loading={sending} onClick={handleSend} disabled={!detailLoaded} className={`${styles.modalBtn} ${styles.modalBtnBlue}`}>
							<Icon name="send" /> Enviar
						</Button>
					</div>
				</Modal.Actions>
			</Modal>
			{confirmCloseOpen && (
				<ConfirmModal
					visible
					icon="exclamation circle"
					header="Confirmar Cerrar"
					buttonLabel="Cerrar"
					content="Tiene cambios sin guardar. ¿Está seguro que desea cerrar?"
					onConfirm={handleConfirmClose}
					onDismiss={() => {
						setConfirmCloseOpen(false);
					}}
				/>
			)}
		</>
	);
};

FlowSendModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	flow: PropTypes.object,
	currentChat: PropTypes.object,
	dispatch: PropTypes.func,
	userName: PropTypes.string,
	onSuccessSend: PropTypes.func,
};

const mapStateToProps = (state) => {
	return {
		userName: state.user.name,
	};
};

export default connect(mapStateToProps)(FlowSendModal);
